import initSqlJs from 'sql.js';
// Import WASM file directly - webpack will handle it
import sqlWasm from 'sql.js/dist/sql-wasm.wasm';

let SQL = null;
let db = null;

/**
 * Initialise SQLite database with schema and seed data
 * Loads sql.js WASM bundled with the app
 */
export const initialiseDatabase = async (schema, seedDataFn) => {
  // Pass the imported WASM file directly
  const config = {
    locateFile: () => sqlWasm
  };

  SQL = await initSqlJs(config);
  db = new SQL.Database();

  // Execute schema DDL
  db.run(schema);

  // Seed data using provided function
  if (seedDataFn) {
    seedDataFn(db);
  }

  return db;
};

/**
 * Execute a SQL query and return results
 */
export const executeQuery = (query) => {
  if (!db) {
    throw new Error('Database not initialised');
  }

  try {
    const results = db.exec(query);
    if (results.length === 0) {
      return { columns: [], values: [] };
    }
    return results[0];
  } catch (err) {
    throw new Error(`SQL Error: ${err.message}`);
  }
};

/**
 * Compare user query results with expected results
 * Returns verdict object with pass/close/fail status and diff
 */
export const compareResults = (userResults, referenceResults) => {
  // Check column count
  if (userResults.columns.length !== referenceResults.columns.length) {
    return {
      verdict: 'fail',
      diff: null,
      message: `Column count mismatch: expected ${referenceResults.columns.length}, got ${userResults.columns.length}`
    };
  }

  // Check row count - if different, it's a fail
  if (userResults.values.length !== referenceResults.values.length) {
    return {
      verdict: 'fail',
      diff: buildRowDiff(userResults.values, referenceResults.values),
      message: `Row count mismatch: expected ${referenceResults.values.length}, got ${userResults.values.length}`
    };
  }

  // Row-by-row comparison
  const diff = [];
  let allMatch = true;

  for (let i = 0; i < referenceResults.values.length; i++) {
    const matches = arraysEqual(userResults.values[i], referenceResults.values[i]);
    diff.push({
      index: i,
      userRow: userResults.values[i],
      refRow: referenceResults.values[i],
      matches
    });
    if (!matches) {
      allMatch = false;
    }
  }

  // If all rows match in order, it's perfect
  if (allMatch) {
    return {
      verdict: 'pass',
      diff,
      message: 'Perfect match!'
    };
  }

  // Rows don't match in order - check if it's just a sorting issue
  // Sort both result sets and compare
  const sortedUser = [...userResults.values].sort(compareRows);
  const sortedRef = [...referenceResults.values].sort(compareRows);

  let dataMismatch = false;
  for (let i = 0; i < sortedRef.length; i++) {
    if (!arraysEqual(sortedUser[i], sortedRef[i])) {
      dataMismatch = true;
      break;
    }
  }

  return {
    verdict: dataMismatch ? 'fail' : 'close',
    diff,
    message: dataMismatch ? 'Incorrect data returned' : 'Correct data but incorrectly sorted'
  };
};

/**
 * Check if two arrays are equal (deep equality)
 */
const arraysEqual = (a, b) => {
  if (a.length !== b.length) return false;
  return a.every((val, i) => {
    // Handle numeric comparison with small tolerance for floating point
    if (typeof val === 'number' && typeof b[i] === 'number') {
      return Math.abs(val - b[i]) < 1e-6;
    }
    return val === b[i];
  });
};

/**
 * Compare two rows for sorting
 * Converts values to strings for consistent comparison
 */
const compareRows = (a, b) => {
  const aStr = JSON.stringify(a);
  const bStr = JSON.stringify(b);
  return aStr.localeCompare(bStr);
};

/**
 * Build row-by-row diff for mismatched result sets
 */
const buildRowDiff = (userRows, refRows) => {
  const maxRows = Math.max(userRows.length, refRows.length);
  return Array.from({ length: maxRows }, (_, i) => ({
    index: i,
    userRow: userRows[i] || null,
    refRow: refRows[i] || null,
    matches: false
  }));
};

/**
 * Reset the database (for switching between tests)
 */
export const resetDatabase = () => {
  if (db) {
    db.close();
    db = null;
  }
};

/**
 * Get current database instance
 */
export const getDatabase = () => db;
